/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {
  Badge,
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { useTranslation } from '@web/src/lib/intl/react'
import { type ComponentProps, forwardRef, type MouseEvent, type PointerEvent } from 'react'

const AI_DETECTION_ENHANCED_MIN = 0.51
const AI_DETECTION_AI_CONTENT_MIN = 0.76

export type AiDetectionTag = 'Enhanced' | 'AI Content'

export function getAiDetectionTag(aiScore: number | null | undefined): AiDetectionTag | null {
  if (aiScore == null || !Number.isFinite(aiScore)) {
    return null
  }

  if (aiScore >= AI_DETECTION_AI_CONTENT_MIN) {
    return 'AI Content'
  }

  if (aiScore >= AI_DETECTION_ENHANCED_MIN) {
    return 'Enhanced'
  }

  return null
}

function stopClickThrough(event: MouseEvent | PointerEvent) {
  event.stopPropagation()
}

interface SFMediaAiDetectionProps extends ComponentProps<typeof Badge> {
  aiScore?: number | null
  popoverAlign?: 'start' | 'center' | 'end'
}

const SFMediaAiDetection = forwardRef<HTMLButtonElement, SFMediaAiDetectionProps>((props, forwardedRef) => {
  const { aiScore, className, size = 'sm', variant = 'outline', popoverAlign = 'end', ...rest } = props
  const { t } = useTranslation()
  const tag = getAiDetectionTag(aiScore)

  if (!tag) {
    return null
  }

  const title = tag === 'AI Content' ? t('AI Content') : t('Enhanced')
  const description =
    tag === 'AI Content'
      ? t('A large part of this image may be AI-generated. Automated detection is a best guess and can be wrong.')
      : t('This photo may include AI enhancements or filters. Automated detection is a best guess and can be wrong.')

  return (
    <Popover>
      <PopoverTrigger
        render={
          <button
            aria-label={title}
            className={cn('pointer-events-auto cursor-pointer p-0', className)}
            onClick={stopClickThrough}
            onPointerDown={stopClickThrough}
            ref={forwardedRef}
            type="button"
          />
        }
      >
        <Badge className="rounded-md bg-black/30 text-white shadow-md" size={size} variant={variant} {...rest}>
          {title}
        </Badge>
      </PopoverTrigger>
      <PopoverContent
        align={popoverAlign}
        className="w-72 gap-0 p-4"
        onClick={stopClickThrough}
        onPointerDown={stopClickThrough}
        side="bottom"
        sideOffset={4}
      >
        <PopoverHeader className="gap-2">
          <PopoverTitle>{title}</PopoverTitle>
          <PopoverDescription className="text-sm">{description}</PopoverDescription>
        </PopoverHeader>
      </PopoverContent>
    </Popover>
  )
})

SFMediaAiDetection.displayName = 'SFMediaAiDetection'

export default SFMediaAiDetection
